import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import { loopStages, supportChips } from '../../data/supportLoop';
import { SectionIntro } from '../layout/SectionIntro';
export function SupportLoop() {
  const topRow = loopStages.slice(0, 3);
  const bottomRow = loopStages.slice(3);
  return <section id="support" className="relative overflow-hidden bg-mist-100 py-16 text-navy-900 lg:py-24" data-id="element-307" data-mp="2rfc29hod6swd">
      <div className="absolute inset-0 bp-light" aria-hidden="true" data-id="element-308" data-mp="2wazq9tr41g0a" />
      <div className="absolute inset-x-0 top-0 h-20 bg-ink" style={{
      clipPath: 'polygon(0 0, 100% 0, 0 100%)'
    }} aria-hidden="true" data-id="element-309" data-mp="31avtvrvwb5fb" />

      <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-310" data-mp="3b1qekrthtwyn">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-311" data-mp="3b3t3f8iwtk1s">
          <SectionIntro className="lg:col-span-7" tone="light" eyebrow="Process & support" title={<>
                Launch day is the easy part.
                <span className="block text-navy-500" data-id="element-313" data-mp="249mxv9zk60h3">We stay on the tools after it.</span>
              </>} body="Most sites go stale because updating them is somebody's least favourite job. So we take it: design, build, launch, hosting, updates, training, tracking, and the next round of improvements." data-id="element-312" data-mp="3f56b7o5kbxbc" />
          <div className="lg:col-span-5 lg:justify-self-end" data-id="element-314" data-mp="2nw1cwrw2nin2">
            <div className="flex items-center gap-4 rounded-2xl border border-navy-900/10 bg-white px-6 py-5" data-id="element-315" data-mp="2nydswzi9rixh">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-forge-orange/10" data-id="element-316" data-mp="2izke93r8xlvq">
                <RotateCcwIcon className="h-6 w-6 text-forge-orange" aria-hidden="true" data-id="element-317" data-mp="297entygn7cin" />
              </span>
              <p className="text-[13.5px] leading-relaxed text-navy-700/80" data-id="element-318" data-mp="36mirztnzefpy">
                It runs as a loop, not a handoff. Step six feeds straight back into step one.
              </p>
            </div>
          </div>
        </div>

        {/* conveyor: row 1 left to right, row 2 right to left, then loops back */}
        <div className="mt-12 space-y-4" data-id="element-319" data-mp="140de52ds7whs">
          <Row stages={topRow} direction="forward" startIndex={0} data-id="element-320" data-mp="13zc0qr9amxww" />
          <div className="hidden justify-end px-6 lg:flex" aria-hidden="true" data-id="element-321" data-mp="q7nu33htd1nb">
            <div className="flex h-8 w-[32%] items-center" data-id="element-322" data-mp="v7a310qlzvqb">
              <span className="h-px flex-1 bg-navy-900/15" data-id="element-323" data-mp="v5pdjpj5j2zb" />
              <span className="h-8 w-px bg-navy-900/15" data-id="element-324" data-mp="g9riybovm6p7" />
            </div>
          </div>
          <Row stages={bottomRow} direction="reverse" startIndex={3} data-id="element-325" data-mp="laoz6iqbsqo2" />
        </div>

        <div className="mt-10 rounded-2xl bg-ink px-6 py-8 lg:px-9" data-id="element-326" data-mp="7ae5l7xmsxz7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-327" data-mp="7c7d0pg2jm89">
            <div data-id="element-328" data-mp="casgcxqmam0x">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-forge-cyan" data-id="element-329" data-mp="21o5eu6c1tfu4">Included on sites we maintain</p>
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-330" data-mp="26lxi1wi58odm">
                {supportChips.map(chip => <span key={chip} className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[12.5px] font-medium text-mist-100" data-id="element-331" data-mp="2groykgtrux4b">
                    {chip}
                  </span>)}
              </div>
            </div>
            <p className="max-w-xs shrink-0 text-[13px] leading-relaxed text-mist-300" data-id="element-332" data-mp="2kqxunscab731">
              Already have a site elsewhere? We can take over hosting, updates, and support without rebuilding it first.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function Row({
  stages,
  direction,
  startIndex
}: {
  stages: typeof loopStages;
  direction: 'forward' | 'reverse';
  startIndex: number;
}) {
  return <ol className={`grid gap-4 lg:grid-cols-3 ${direction === 'reverse' ? 'lg:[direction:rtl]' : ''}`} data-id="element-333" data-mp="1e425t5zzui5c">
      {(direction === 'reverse' ? [...stages].reverse() : stages).map((stage, i) => <motion.li key={stage.index} initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.3
    }} transition={{
      duration: 0.4,
      delay: i * 0.07
    }} className="relative rounded-2xl border border-navy-900/10 bg-white p-6 [direction:ltr]" data-id="element-334" data-mp="1bjmfrltgh4od" data-mpi={i}>
          <div className="flex items-center gap-3" data-id="element-335" data-mp="1pi3zl8g8px10">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-navy-900 font-display text-[12px] font-extrabold text-white" data-id="element-336" data-mp="st4lhcsz610w">
              {stage.index}
            </span>
            <h3 className="font-display text-[15.5px] font-bold text-navy-900" data-id="element-337" data-mp="wsdh6n7ks8z3">{stage.title}</h3>
          </div>
          <p className="mt-3 text-[13.5px] leading-relaxed text-navy-700/75" data-id="element-338" data-mp="16kj8rvtx0odr">{stage.body}</p>

          {i !== stages.length - 1 && <span className="absolute -right-4 top-1/2 hidden -translate-y-1/2 lg:block" aria-hidden="true" data-id="element-339" data-mp="9442044vajgx">
              <ArrowRightIcon className={`h-4 w-4 text-forge-orange ${direction === 'reverse' ? 'rotate-180' : ''}`} data-id="element-340" data-mp="e1vtix1lo3kl" />
            </span>}
          <span className="absolute inset-x-0 bottom-0 h-[2px] rounded-b-2xl bg-forge-orange/70" style={{
        width: `${(startIndex + i + 1) / 6 * 100}%`
      }} aria-hidden="true" data-id="element-341" data-mp="1mfkfsrajpm1" />
        </motion.li>)}
    </ol>;
}